import { Injectable } from "@angular/core";
import { config } from "../../environments/config";
import { HttpService } from "./http.service";
import { LocalStorageService } from "./local-storage.service";
import { API_ENDPOINTS } from "../api/api-endpoints";
import { ExportHeader, ExportService, ExportSheet } from "./export.service";
import { ApplicationEventsFacade } from "../state/application-events.facade";
import { CopilotStateFacade } from "../state/copilot-state.facade";
import { PhoStateFacade } from "../state/pho-state.facade";
import { map } from "rxjs";

const ACCESS_TOKEN_EXPIRY_DURATION = 5;

const status = [
  "started",
  "not_started",
  "pending",
  "in_progress",
  "completed",
  "success",
  "failed",
];
const xStatus = {
  started: "started",
  notStarted: "not_started",
  pending: "pending",
  inProgress: "in_progress",
  completed: "completed",
  failed: "failed",
  failure: "failure",
  success: "success",
};

@Injectable({
  providedIn: "root",
})
export class StateService {
  readonly downloadToastEvents$ = this.applicationEvents.downloadToastEvents$;
  /** @deprecated Use downloadToastEvents$. */
  readonly Result = this.downloadToastEvents$;
  readonly copilotConfig$ = this.copilotState.config$;
  readonly data$ = this.applicationEvents.sharedData$;
  readonly callSetLevel = this.phoState.level$;
  createNewExp = false;
  guideLayerShow = false;
  hashChanged = false;
  apiList = API_ENDPOINTS;
  status = status;
  xStatus = xStatus;
  isDownload = false;
  calledApiList: any = [];
  // unused subjects / messages removed
  permissions = {
    tag_user_role: "users.roles.tag",
    untag_user_role: "users.roles.untag",

    admin_users_list: "admin.users.list",
    admin_requests_view: "admin.requests.view",
    admin_requests_approve: "admin.requests.approve",

    zone_users_list: "zones.users.list",
    zone_users_add: "zones.users.add",
    zone_users_migrate: "zones.users.migrate",
    zone_users_revoke: "zones.users.revoke",

    zone_users_key_account_sync: "zones.users.key-accounts.sync",
    zone_requests_view: "zones.requests.view",
    zone_requests_approve: "zones.requests.approve",

    zones_add: "zones.add",
    zones_edit: "zones.edit",
    zones_delete: "zones.delete",

    key_accounts_add: "key-accounts.add",
    key_accounts_edit: "key-accounts.edit",
    key_accounts_delete: "key-accounts.delete",

    roles_add: "roles.add",
    roles_edit: "roles.edit",
    roles_delete: "roles.delete",

    permissions_add: "permissions.add",
    permissions_edit: "permissions.edit",
    permissions_delete: "permissions.delete",
  };
  // removed large unused `process` and `searchCols` objects
  monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  // country/keyacc/region removed (not used)
  readonly downloadProgressForSimulation =
    this.applicationEvents.downloadProgress$;
  readonly softWarningToastEvents$ =
    this.applicationEvents.softWarningToastEvents$;
  /** @deprecated Use softWarningToastEvents$. */
  readonly openSoftWarningToast = this.softWarningToastEvents$;
  individualProcess: any;
  // removed unused PDF/pdfGeneration/zoom/assort fields
  levelsKeyOrder: any = ["Country", "State", "Region", "Channel"];
  kbFactor = 1e3;
  mbFactor = 1e6;
  gbFactor = 1e9;
  allowedFileType: any = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "application/wps-office.xlsx",
    "text/csv",
  ];
  setStatesCall: boolean = false;
  setStatesList: any = [];
  experimentTypes: any = [
    "assortment",
    "space-assortment",
    "data-exploration",
    "clustering",
    "pho",
    "global-dashboard",
    "pho_dtp",
  ];

  constructor(
    private http: HttpService,
    private localStorageService: LocalStorageService,
    private exportService: ExportService,
    private applicationEvents: ApplicationEventsFacade,
    private copilotState: CopilotStateFacade,
    private phoState: PhoStateFacade,
  ) {}

  get copilotConfig(): unknown {
    return this.copilotState.config;
  }

  get getAssortmentLock() {
    return this.phoState.assortmentLock$;
  }

  setAssortmentLock(newValue: unknown): void {
    this.phoState.setAssortmentLock(newValue);
  }

  setCopilotConfig(config: unknown): void {
    this.copilotState.setConfig(config);
  }

  checkAppConfig() {
    return config?.app;
  }

  getAppDetails() {
    return config;
  }

  addCalledApi(url: string) {
    setTimeout(() => {
      this.calledApiList.push(url);
    }, 0);
  }

  removeCalledApi(url: string) {
    setTimeout(() => {
      const index = this.calledApiList.indexOf(url);
      if (index !== -1) {
        this.calledApiList.splice(index, 1);
      }
    }, 0);
  }
  popOutCalledApi() {
    setTimeout(() => {
      this.calledApiList.pop();
    }, 0);
  }

  titleCase = (text: string = "") => {
    return (
      text &&
      typeof text === "string" &&
      text
        ?.split("_")
        .map((item) => item.charAt(0).toUpperCase() + item.substr(1))
        ?.join(" ")
    );
  };

  titleFirstLetterCaps = (text: string = "") =>
    text &&
    typeof text === "string" &&
    text
      .split("_")
      .map((item, index) =>
        index === 0 ? item.charAt(0).toUpperCase() + item.substr(1) : item,
      )
      ?.join(" ");

  expTypeTransform(value: string) {
    if (!value) {
      return "";
    }
    return value.replace(/[\s_]+/g, "-").toLowerCase();
  }

  nFormatter(num: number) {
    if (+num >= 1000000000) {
      return (+num / 1000000000).toFixed().replace(/\.0$/, "") + "B";
    }
    if (+num >= 1000000) {
      return (+num / 1000000).toFixed().replace(/\.0$/, "") + "M";
    }
    if (+num >= 1000) {
      return (+num / 1000).toFixed().replace(/\.0$/, "") + "K";
    }
    return num;
  }

  nFormatterDecimal(num: number) {
    const isNegative = num < 0;
    num = Math.abs(num); // Take the absolute value

    let formattedNum;
    if (num >= 1000000000) {
      formattedNum = (num / 1000000000).toFixed(1) + "B";
    } else if (num >= 1000000) {
      formattedNum = (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      formattedNum = (num / 1000).toFixed(1) + "K";
    } else {
      formattedNum = num.toString();
    }

    return isNegative ? "-" + formattedNum : formattedNum;
  }

  nFormatterVolume(num: number, type: string) {
    const isNegative = num < 0;
    num = Math.abs(num); // Take the absolute value

    let formattedNum;
    if (type === "Total Volume" || type === "Volume") {
      if (num >= 1000000000) {
        formattedNum =
          (num / 1000000000).toFixed().replace(/\.0$/, "") + "B HL";
      } else if (num >= 1000000) {
        formattedNum = (num / 1000000).toFixed().replace(/\.0$/, "") + "M HL";
      } else if (num >= 1000) {
        formattedNum = (num / 1000).toFixed().replace(/\.0$/, "") + "K HL";
      } else {
        formattedNum = num.toString();
      }
    } else {
      formattedNum = num.toString();
    }

    return isNegative ? "-" + formattedNum : formattedNum;
  }

  nFormatterVolumeDecimal(num: number, type: string) {
    const isNegative = num < 0;
    num = Math.abs(num); // Take the absolute value

    let formattedNum;
    if (type === "Total Volume") {
      if (num >= 1000000000) {
        formattedNum = (num / 1000000000).toFixed(1) + "B HL";
      } else if (num >= 1000000) {
        formattedNum = (num / 1000000).toFixed(1) + "M HL";
      } else if (num >= 1000) {
        formattedNum = (num / 1000).toFixed(1) + "K HL";
      } else {
        formattedNum = num.toString();
      }
    } else {
      formattedNum = num.toString();
    }

    return isNegative ? "-" + formattedNum : formattedNum;
  }

  nParseFloat(num: number) {
    return num?.toFixed(2);
  }

  setTokenExpiryDate() {
    window.localStorage.removeItem("tokenExpiryDate");

    const tokenExpiryDuration: number = ACCESS_TOKEN_EXPIRY_DURATION;
    const currentDate = new Date();
    const tokenExpiryDate = new Date(currentDate.getTime());

    tokenExpiryDate.setMinutes(currentDate.getMinutes() + tokenExpiryDuration);
    window.localStorage.setItem("tokenExpiryDate", tokenExpiryDate.toString());
  }

  getToken() {
    return window.localStorage.c;
  }
  openToastService(result: unknown): void {
    this.applicationEvents.showDownloadToast(result);
  }
  setDownloadProgress(status: unknown): void {
    this.applicationEvents.setDownloadProgress(status);
  }

  clearPlanoV2LocalStorage() {
    sessionStorage.removeItem("plansDetails");
    sessionStorage.removeItem("selectedPlanogramDetails");
    sessionStorage.removeItem("selectedPlanOfPlano");
    sessionStorage.removeItem("experimentMode");
  }

  getExistingPlan(url: any) {
    return this.http.getMethod(url);
  }

  //open Soft warning toast
  openSoftWarningToastService(message: string): void {
    const storedMarginClass = sessionStorage.getItem("toastMarginClass");
    const marginClass =
      storedMarginClass === "right-toolbar-close"
        ? "right-toolbar-close"
        : "right-toolbar-open";
    this.applicationEvents.showSoftWarning(message, marginClass);
  }
  getBulkUserDetailsService(payload: any) {
    const url = this.apiList.user.sharedWith;
    return this.http.postMethod(url, payload);
  }

  //terms and conditions
  acceptTermsAndondition(params: any) {
    const url = this.apiList.user.acceptTerms;
    return this.http.putMethod(url, params);
  }

  getExperimentDetailsByExpId() {
    const expId = sessionStorage.getItem("expId");
    const url = `${this.apiList.experiments.getExperiments}/${expId}`;
    return this.http.getMethod(url);
  }

  getSidebarStatesByExpId(refId?: any) {
    const expId = sessionStorage.getItem("expId");
    const url = `${this.apiList.experiments.goProcessStates}${refId ? refId : expId}/states`;
    return this.http.getMethod(url).pipe(
      map((response: any) => ({
        ...response,
        states: Array.isArray(response?.states)
          ? response.states
          : Array.isArray(response)
            ? response
            : response?.title
              ? [response]
              : [],
      })),
    );
  }

  getFileName(filename: any, extension: any) {
    const expDetails = JSON.parse(sessionStorage.getItem("expDetails")!)[0];
    if (expDetails?.keyAccount) {
      expDetails.keyAccount = expDetails.keyAccount.split(" ").join("_");
    }
    expDetails.expName = expDetails.expName.split(" ").join("_");
    const date = new Date().toISOString().substring(0, 10).split("-").join("");
    const countryPrefix = expDetails?.expCountry
      ? `${expDetails.expCountry}_`
      : "";
    if (expDetails?.keyAccount) {
      expDetails.keyAccount = expDetails.keyAccount.split(" ").join("_");
      return (
        countryPrefix +
        expDetails.keyAccount +
        "_" +
        expDetails.expName +
        "_" +
        filename +
        "_" +
        date +
        "." +
        extension
      );
    } else {
      return (
        countryPrefix +
        expDetails.expName +
        "_" +
        filename +
        "_" +
        date +
        "." +
        extension
      );
    }
  }

  createProcessUrl(task_id: string) {
    return `${this.apiList.experiments.goProcess}/${task_id}/status`;
  }

  // Formatting logic for Volume
  formatVolumeLabel(num: number): string {
    if (num < 0) {
      // If the number is negative, remove the sign and format the absolute value
      return "- " + this.formatVolumeLabel(-num);
    }

    if (+num >= 1000000000 && +num <= 999999999999) {
      return (+num / 1000000000).toFixed(1) + "B HL";
    }
    if (+num >= 1000000 && +num < 999999999) {
      return (+num / 1000000).toFixed(1) + "M HL";
    }
    if (+num >= 1000 && +num < 999999) {
      return (+num / 1000).toFixed(1) + "K HL";
    }
    return `${num}`;
  }

  // Formatting logic for Volume
  formatTrendVolumeLabel(num: number): string {
    if (num < 0) {
      // If the number is negative, remove the sign and format the absolute value
      return "- " + this.formatRevenueLabel(-num);
    }
    if (num >= 1000000000) {
      return `${(num / 1000000000).toFixed(1)} B`;
    }
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)} M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)} K`;
    }
    return `${num}`;
  }

  // Formatting logic for Revenue and Maco
  formatRevenueLabel(num: any): string {
    if (num < 0) {
      // If the number is negative, remove the sign and format the absolute value
      return "- " + this.formatRevenueLabel(-num);
    }
    if (num >= 1000000000) {
      return `$${(num / 1000000000).toFixed(1)} B`;
    }
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(1)} M`;
    }
    if (num >= 1000) {
      return `$${(num / 1000).toFixed(1)} K`;
    }
    return `$${num}`;
  }

  sendStatesCall(apiFlag: boolean) {
    this.phoState.requestStatesReset(apiFlag);
  }

  getStatesCall() {
    return this.phoState.statesReset$;
  }

  updateSidebarState(value: boolean) {
    this.phoState.setSidebarState(value);
  }

  isSidebarUpdated() {
    return this.phoState.sidebarState$;
  }
  getStatesList(states: any) {
    this.setStatesList = states;
  }

  checkStatesFlag(routerSlug: string) {
    this.setStatesList.map((item: any) => {
      if (item.slug === routerSlug) {
        this.setStatesCall = item.status === "completed" ? true : false;
      }
    });
    return this.setStatesCall;
  }

  parseFileSize(sizeInBytes: any) {
    const factor =
      sizeInBytes > this.gbFactor
        ? this.gbFactor
        : sizeInBytes > this.mbFactor
          ? this.mbFactor
          : this.kbFactor;
    const suffix =
      factor === this.gbFactor
        ? " GB"
        : factor === this.mbFactor
          ? " MB"
          : " KB";
    const size = sizeInBytes / factor;
    return String(parseFloat(size.toFixed(1))) + suffix;
  }

  checkExperimentType() {
    const expType = sessionStorage.getItem("expType") || "";
    return this.expTypeTransform(expType) || null;
  }

  /** @deprecated Inject ExportService directly in new code. */
  exportToExcel(
    excelHeader: readonly ExportHeader[],
    excelData: readonly unknown[],
    fileName: string,
  ): void {
    this.exportService.exportToExcel(excelHeader, excelData, fileName);
  }

  /** @deprecated Inject ExportService directly in new code. */
  exportToExcelSheets(sheets: readonly ExportSheet[], fileName: string): void {
    this.exportService.exportToExcelSheets(sheets, fileName);
  }

  /** @deprecated Inject ExportService directly in new code. */
  parseTableData(
    excelHeader: readonly ExportHeader[],
    excelData: readonly unknown[],
  ) {
    return this.exportService.parseTableData(excelHeader, excelData);
  }

  sendData(data: unknown): void {
    this.applicationEvents.setSharedData(data);
  }

  // Helper function convert the number
  nParseFloatNumber(val: any) {
    if (typeof val === "string") {
      return val !== "" ? val : "-";
    } else if (this.isInt(val)) {
      return this.formatNumber(val);
    } else if (val && typeof val !== "string" && !this.isInt(val)) {
      const floatVal: any = this.nParseFloat(val);
      return this.formatNumber(floatVal);
    } else {
      return val || "-";
    }
  }

  // Helper function for check the number integer
  isInt(num: any) {
    return Number(num) === num && num % 1 === 0;
  }

  // Helper function for format the number
  formatNumber(num: number): string {
    if (typeof num !== "number" || !isFinite(num)) return "";
    const [integerPart, decimalPart] = Math.abs(num).toString().split(".");
    const safeInteger = integerPart.replace(/\d(?=(\d{3})+$)/g, "$&,");
    return (
      (num < 0 ? "-" : "") +
      safeInteger +
      (decimalPart ? "." + decimalPart : "")
    );
  }

  // Helper function for find the zone name
  getZoneName(zoneID: string, zoneList: any) {
    const item = zoneList.find(
      (item: any) => item.id === zoneID || item.name === zoneID,
    );
    return item?.name;
  }
  getGlobalDashboardData(expId: any, isOlderExperiment: boolean) {
    const url = this.apiList.dashboard.getDashboard(expId, isOlderExperiment);
    return this.http.getMethod(url);
  }

  getUserAccessType() {
    const userId = JSON.parse(sessionStorage.getItem("user-name")!)?.id;
    const userDetailsId = sessionStorage.getItem("user");
    const storedAccessType = sessionStorage.getItem("access_type");

    // If accessType already exists in session storage, return that.
    if (storedAccessType) {
      return storedAccessType;
    } else {
      // Otherwise, determine the access type and store it.
      const accessType = userId === userDetailsId ? "edit" : "read";
      sessionStorage.setItem("access_type", accessType);
      return accessType;
    }
  }

  // Helper method to getting the experiment details from session storage
  getExpDetails(): any | null {
    const expDetailsString = sessionStorage.getItem("expDetails");
    if (expDetailsString) {
      const expDetails = JSON.parse(expDetailsString);
      return Array.isArray(expDetails) ? expDetails[0] : null;
    }
    return null;
  }
}
